import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import type {
  Capability,
  CapabilityCreateInput,
  CapabilityHistoryEntry,
  CapabilityUpdateInput,
  CapabilityUsage,
} from '../types/capability'
import { INVALIDATED_PREFIXES } from './useDemands'

export function useCapabilities(focusArea?: string, includeCrossFa = false) {
  return useQuery<Capability[]>({
    queryKey: ['capabilities', focusArea ?? 'all', includeCrossFa],
    queryFn: async () => {
      const params: Record<string, string | boolean> = {}
      if (focusArea) params.focus_area = focusArea
      if (focusArea && includeCrossFa) params.include_cross_fa = true
      const res = await api.get('/capabilities', { params })
      return res.data
    },
    staleTime: 5 * 60_000,
  })
}

/**
 * POST /capabilities — the curation door, and since #1344 the only one. Admins
 * only, now that the catalog has categories. `focus_area_code` must be SENT: a
 * code, or `null` for a shared entry. A duplicate is refused naming the
 * existing entry (#1349); omitting the key is a 422.
 */
export function useCreateCapability() {
  const qc = useQueryClient()
  return useMutation<Capability, Error, CapabilityCreateInput>({
    mutationFn: async (input) => {
      const res = await api.post('/capabilities', input)
      return res.data as Capability
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['capabilities'] })
      qc.invalidateQueries({ queryKey: ['skill-suggestions'] })
    },
  })
}

export function useUpdateCapability() {
  const qc = useQueryClient()
  return useMutation<Capability, Error, { id: number; body: CapabilityUpdateInput }>({
    mutationFn: async ({ id, body }) => {
      const res = await api.patch(`/capabilities/${id}`, body)
      return res.data as Capability
    },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ['capabilities'] })
      qc.invalidateQueries({ queryKey: ['capability-history', vars.id] })
      qc.invalidateQueries({ queryKey: ['skill-suggestions'] })
      // A rename rewrites the word inside `demands.skills`, so every cache
      // holding a demand is stale — the list the council is looking at
      // included. Reuses the demand hooks' own prefix list rather than a
      // second copy of it.
      if (data?.demands_repointed) {
        for (const prefix of INVALIDATED_PREFIXES) {
          qc.invalidateQueries({ queryKey: [prefix], exact: false })
        }
      }
    },
  })
}

export function useCapabilityUsage(id: number | null) {
  return useQuery<CapabilityUsage>({
    queryKey: ['capability-usage', id],
    queryFn: async () => {
      const res = await api.get(`/capabilities/${id}/usage`)
      return res.data
    },
    enabled: id !== null,
    staleTime: 30_000,
  })
}

export function useCapabilityHistory(id: number | null) {
  return useQuery<CapabilityHistoryEntry[]>({
    queryKey: ['capability-history', id],
    queryFn: async () => {
      const res = await api.get(`/capabilities/${id}/history`)
      return res.data
    },
    enabled: id !== null,
    staleTime: 30_000,
  })
}
